import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  IconButton,
  InputAdornment,
  Checkbox,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '../assets/google.svg';

import { signUp } from "../services/authService";
import { COGNITO_CONFIG } from "../../Utils/constants";
import { markLoginInProgress } from '../../Utils/postLoginRedirect';
import { typography } from '../../styles/theme';
import { validatePassword } from '../utils/passwordValidation';
import {
  authColors,
  authTitleSx,
  authSubtitleSx,
  authLabelSx,
  authTextFieldSx,
  authInputIconSx,
  authLinkAccentSx,
  authPrimaryButtonSx,
  authGoogleButtonSx,
  authPanelFlexSx,
  authCloseButtonSx,
  authFieldErrorSx,
  authSecondaryLinkRowSx,
  isValidEmail,
} from '../styles/authPanel.styles';

const registerTitleSx = {
  ...authTitleSx,
  mt: 0,
  mb: 0,
};

const registerSubtitleSx = {
  ...authSubtitleSx,
  mt: '12px',
  mb: 0,
};

const registerPanelSx = {
  ...authPanelFlexSx,
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
};

const registerContentSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: '100%',
  pb: { xs: '12px', sm: '16px' },
};

const registerHeaderSx = {
  flexShrink: 0,
  width: '100%',
  mb: '64px',
};

const registerBodySx = {
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '100%',
};

const registerTermsRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  mt: '20px',
  mb: '20px',
  width: '100%',
};

const registerTermsTextSx = {
  flex: 1,
  minWidth: 0,
  fontFamily: typography.fontFamily.primary,
  fontSize: '16px',
  fontWeight: typography.fontWeight.regular,
  lineHeight: '20px',
  color: authColors.textWhite,
};

function RegisterOrDivider() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        my: '12px',
      }}
    >
      <Box sx={{ flex: 1, height: '1px', backgroundColor: authColors.divider }} />
      <Typography
        component="span"
        sx={{
          fontFamily: typography.fontFamily.primary,
          fontSize: '16px',
          fontWeight: typography.fontWeight.regular,
          lineHeight: '16px',
          color: authColors.textMuted,
          whiteSpace: 'nowrap',
        }}
      >
        o con tu email
      </Typography>
      <Box sx={{ flex: 1, height: '1px', backgroundColor: authColors.divider }} />
    </Box>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <Typography component="label" sx={authLabelSx}>
      {children}
      {required && (
        <Typography component="span" sx={{ color: authColors.required }}>
          *
        </Typography>
      )}
    </Typography>
  );
}

function PasswordToggleButton({ show, onToggle, disabled, labelShow, labelHide }) {
  return (
    <IconButton
      aria-label={show ? labelHide : labelShow}
      onClick={onToggle}
      onMouseDown={(e) => e.preventDefault()}
      edge="end"
      size="small"
      disabled={disabled}
      sx={{
        color: 'rgba(255,255,255,0.72)',
        '&:hover': { backgroundColor: 'transparent', color: authColors.textWhite },
      }}
    >
      {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  );
}

const TERMS_URL = 'https://terrio.net/terms';

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    if (emailError && (value.trim() === '' || isValidEmail(value))) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError('');
      return;
    }
    setEmailError(isValidEmail(trimmed) ? '' : 'Ingresá un email válido');
  };

  const handleGoogleSignIn = () => {
    try {
      markLoginInProgress(true);

      const { clientId, cognitoDomain, callbackUrl } = COGNITO_CONFIG;
      const oauthScopes =
        process.env.REACT_APP_COGNITO_OAUTH_SCOPES || "email openid profile";
      const redirectUri = callbackUrl;
      const state = Math.random().toString(36).substring(2, 15) +
                   Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('oauth_state', state);

      const googleAuthUrl =
        `https://${cognitoDomain}/oauth2/authorize?` +
        `client_id=${clientId}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(oauthScopes)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `identity_provider=Google&` +
        `state=${state}&` +
        `prompt=select_account`;

      window.location.href = googleAuthUrl;
    } catch (signInError) {
      console.error('Error iniciando Google Sign-In:', signInError);
      setError('Error al iniciar sesión con Google. Verifica la configuración.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    setError('');
    setPasswordError('');
    setConfirmPasswordError('');

    const trimmedEmail = email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setEmailError('Ingresá un email válido');
      return;
    }

    if (!acceptedTerms) {
      setError('Debés aceptar los términos y condiciones para continuar');
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Las contraseñas no coinciden');
      return;
    }

    const passwordValidationError = validatePassword(password);
    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    setLoading(true);
    setError("");
    setPasswordError("");
    setConfirmPasswordError("");

    try {
      const result = await signUp(trimmedEmail, password, name);

      if (result.success) {
        navigate("/verify-email", { state: { email: trimmedEmail } });
      } else {
        console.error("Registration failed:", result.error);
        setError(result.error || "Error al crear la cuenta");
      }
    } catch (err) {
      console.error("Exception during registration:", err);
      setError(err.message || "Error al crear la cuenta");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    email.trim() &&
    password &&
    confirmPassword &&
    acceptedTerms &&
    !emailError &&
    !loading;

  return (
    <Paper elevation={0} sx={registerPanelSx}>
      <IconButton
        size="small"
        onClick={() => navigate("/app")}
        aria-label="Cerrar"
        sx={authCloseButtonSx}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={registerContentSx}>
        <Box sx={registerHeaderSx}>
          <Typography component="h1" sx={registerTitleSx}>
            Creá tu cuenta en Terrio
          </Typography>
          <Typography component="p" sx={registerSubtitleSx}>
            Ingresá tu email y contraseña para empezar.
          </Typography>
        </Box>

        <Box sx={registerBodySx}>
          <Button
            variant="contained"
            fullWidth
            disableElevation
            onClick={handleGoogleSignIn}
            disabled={loading}
            startIcon={<img src={GoogleIcon} alt="Google" width={18} height={18} />}
            sx={authGoogleButtonSx}
          >
            Continuar con Google
          </Button>

          <RegisterOrDivider />

          <Box
            component="form"
            onSubmit={handleRegister}
            sx={{ width: '100%' }}
          >
            <Box sx={{ mb: '16px' }}>
              <FieldLabel required>Email</FieldLabel>
              <TextField
                placeholder="usuario@email.com"
                variant="outlined"
                fullWidth
                type="email"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                disabled={loading}
                autoComplete="email"
                error={!!emailError}
                sx={authTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                      <MailOutlineIcon sx={authInputIconSx} />
                    </InputAdornment>
                  ),
                }}
              />
              {emailError && (
                <Typography sx={authFieldErrorSx}>{emailError}</Typography>
              )}
            </Box>

            <Box sx={{ mb: '16px' }}>
              <FieldLabel>Nombre completo</FieldLabel>
              <TextField
                placeholder="Tu nombre"
                variant="outlined"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                autoComplete="name"
                sx={authTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                      <PersonOutlineIcon sx={authInputIconSx} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: '16px' }}>
              <FieldLabel required>Contraseña</FieldLabel>
              <TextField
                placeholder="Ingresá tu contraseña"
                variant="outlined"
                fullWidth
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                  if (confirmPasswordError) setConfirmPasswordError('');
                }}
                disabled={loading}
                autoComplete="new-password"
                error={!!passwordError}
                sx={authTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                      <LockOutlinedIcon sx={authInputIconSx} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: '4px' }}>
                      <PasswordToggleButton
                        show={showPassword}
                        onToggle={() => setShowPassword((v) => !v)}
                        disabled={loading}
                        labelShow="Mostrar contraseña"
                        labelHide="Ocultar contraseña"
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {passwordError && (
                <Typography sx={authFieldErrorSx}>{passwordError}</Typography>
              )}
            </Box>

            <Box sx={{ mb: '8px' }}>
              <FieldLabel required>Confirmar contraseña</FieldLabel>
              <TextField
                placeholder="Ingresá tu contraseña"
                variant="outlined"
                fullWidth
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmPasswordError) setConfirmPasswordError('');
                }}
                disabled={loading}
                autoComplete="new-password"
                error={!!confirmPasswordError}
                sx={authTextFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                      <LockOutlinedIcon sx={authInputIconSx} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: '4px' }}>
                      <PasswordToggleButton
                        show={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword((v) => !v)}
                        disabled={loading}
                        labelShow="Mostrar confirmación de contraseña"
                        labelHide="Ocultar confirmación de contraseña"
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {confirmPasswordError && (
                <Typography sx={authFieldErrorSx}>{confirmPasswordError}</Typography>
              )}
            </Box>

            <Box sx={registerTermsRowSx}>
              <Checkbox
                size="small"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                disabled={loading}
                sx={{
                  color: authColors.checkboxWhite,
                  borderRadius: '4px',
                  flexShrink: 0,
                  p: 0,
                  '&.Mui-checked': {
                    color: authColors.linkAccent,
                  },
                }}
              />
              <Typography component="span" sx={registerTermsTextSx}>
                Acepto{' '}
                <Link
                  href={TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    ...authLinkAccentSx,
                    display: 'inline',
                    wordBreak: 'break-word',
                  }}
                >
                  términos y condiciones
                </Link>
              </Typography>
            </Box>

            {error && (
              <Typography sx={{ ...authFieldErrorSx, mb: '12px' }}>
                {error}
              </Typography>
            )}

            <Box sx={{ mt: '24px' }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disableElevation
                disabled={!canSubmit}
                sx={authPrimaryButtonSx}
              >
                {loading ? "Creando cuenta..." : "Crear cuenta"}
              </Button>

              <Box sx={authSecondaryLinkRowSx}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: '16px',
                    fontWeight: typography.fontWeight.regular,
                    color: authColors.textWhite,
                    lineHeight: '20px',
                  }}
                >
                  ¿Ya tienes una cuenta?
                </Typography>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate("/login")}
                  sx={authLinkAccentSx}
                >
                  Iniciá sesión
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default Register;
